import ClickableLink from 'components/Common/Clickable/ClickableLink'
import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import Drop from 'LuxKit/Drop'
import type { ImageMediaSource } from 'components/Common/Image/Image'
import { breakpointSizes, mediaQueryUp } from 'components/utils/breakpoint'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import BodyText, { BodyTextMedium } from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { LOYALTY_CREDIT_CARD_PLACEMENTS } from 'analytics/snowplow/events/loyaltyCreditCardEvents'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import { LOYALTY_CREDIT_CARD_PAGE } from 'loyalty/constants/loyaltyRoutes'
import { useLoyaltyCreditCardFeatureFlag } from 'loyalty/hooks/useLoyaltyCreditCardFeatureFlag'
import useLoyaltyCreditCardPlacementTracking from 'loyalty/hooks/useLoyaltyCreditCardPlacementTracking'
import { rem } from 'polished'
import variables from 'styles/vendor/bootstrap/variables'
import * as React from 'react'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled, { css } from 'styled-components'

/* Desktop dimensions mirror the 900x272 design frame within the 932px layout container. */
const DESKTOP_HEIGHT = 272
const DESKTOP_PADDING = 40
const DESKTOP_CONTENT_MAX_WIDTH = 400

/* Position the 527px Drop beyond the banner so clipping exposes only its left arc and centre. */
const DESKTOP_IMAGE_DIAMETER = 527
const DESKTOP_IMAGE_INSET_END = -86
const DESKTOP_IMAGE_INSET_TOP = -112

const MOBILE_PADDING_BLOCK = 28
const MOBILE_PADDING_INLINE = 20
/* Mobile and tablet display the complete Drop beside the copy. */
const MOBILE_IMAGE_DIAMETER = 106
const MOBILE_IMAGE_GAP = 16
/* Reserve inline space for the out-of-flow Drop below desktop. */
const MOBILE_CONTENT_INSET_END = MOBILE_IMAGE_DIAMETER + MOBILE_IMAGE_GAP
const TABLET_IMAGE_DIAMETER = 160

/* Desktop uses a wider crop. Its sand-colour lower padding remains below the banner's clipped edge. */
const DESKTOP_IMAGE_ID = 'societe-credit-card-banner-drop-large'

/* Mobile and tablet reveal the complete Drop, so reuse the self-contained homepage crop. */
const MOBILE_IMAGE_ID = 'societe-credit-card-banner-drop-mobile'

/* `<picture>` selects the first matching source: desktop, then tablet, then the mobile fallback.
   Each breakpoint requests dimensions close to its rendered size. */
const BANNER_IMAGE_SOURCES: Array<ImageMediaSource> = [
  {
    media: `(min-width: ${breakpointSizes.desktop.min}px)`,
    id: DESKTOP_IMAGE_ID,
    width: DESKTOP_IMAGE_DIAMETER,
    dpr: 3,
  },
  {
    media: `(min-width: ${breakpointSizes.tablet.min}px)`,
    id: MOBILE_IMAGE_ID,
    width: TABLET_IMAGE_DIAMETER,
    dpr: 2,
  },
]

/* Supporting copy uses 12px on mobile and LuxKit's 14px medium token from tablet. */
const responsiveBodyText = css`
  ${mediaQueryUp.tablet} {
    ${BodyTextMedium}
  }
`

const Section = styled(Pane)`
  ${mediaQueryUp.tablet} {
    padding: ${rem(20)} 0;
  }
`

const Banner = styled(ClickableLink)`
  position: relative;
  display: block;
  overflow: hidden;
  background-color: ${props => props.theme.palette.highlight.primary.darkBackground};
  color: ${props => props.theme.palette.neutral.default.eight};
  padding: ${rem(MOBILE_PADDING_BLOCK)} ${rem(MOBILE_PADDING_INLINE)};
  /* Cancel LayoutContainer gutters for the full-bleed mobile banner. */
  margin-inline: -${rem(variables.gutterWidth / 2)};

  ${mediaQueryUp.tablet} {
    margin-inline: 0;
    border-radius: ${props => props.theme.borderRadius.M};
  }

  ${mediaQueryUp.desktop} {
    height: ${rem(DESKTOP_HEIGHT)};
    padding: ${rem(DESKTOP_PADDING)};
  }
`

/* Override width directly because Drop's inline `--drop-size` cannot vary by breakpoint. Keep
   `size` at the mobile diameter to size the fallback image request. */
const BannerDrop = styled(Drop)`
  position: absolute;
  top: ${rem(MOBILE_PADDING_BLOCK)};
  inset-inline-end: ${rem(MOBILE_PADDING_INLINE)};
  width: ${rem(MOBILE_IMAGE_DIAMETER)};

  /* Vertically centre the tablet Drop to balance banner clearance. */
  ${mediaQueryUp.tablet} {
    top: 50%;
    margin-block-start: -${rem(TABLET_IMAGE_DIAMETER / 2)};
    width: ${rem(TABLET_IMAGE_DIAMETER)};
  }

  ${mediaQueryUp.desktop} {
    top: ${rem(DESKTOP_IMAGE_INSET_TOP)};
    /* Clear the tablet centring offset before applying the desktop inset. */
    margin-block-start: 0;
    inset-inline-end: ${rem(DESKTOP_IMAGE_INSET_END)};
    width: ${rem(DESKTOP_IMAGE_DIAMETER)};
  }
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  padding-inline-end: ${rem(MOBILE_CONTENT_INSET_END)};

  ${mediaQueryUp.tablet} {
    max-width: none;
    padding-inline-end: ${rem(TABLET_IMAGE_DIAMETER + MOBILE_IMAGE_GAP)};
  }

  ${mediaQueryUp.desktop} {
    max-width: ${rem(DESKTOP_CONTENT_MAX_WIDTH)};
    padding-inline-end: 0;
  }
`

const Eyebrow = styled(BodyText)`
  ${responsiveBodyText}
  color: ${props => props.theme.palette.highlight.primary.normalForeground};
`

const BenefitList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
  margin: ${rem(12)} 0 0;
  padding: 0;
  list-style: none;

  ${mediaQueryUp.desktop} {
    margin-top: ${rem(16)};
  }
`

const Benefit = styled.li`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
`

const BenefitText = styled(BodyText)`
  ${responsiveBodyText}
`

/* The visual CTA is non-interactive because Banner is the only link and tab stop. */
const CtaPill = styled.span`
  display: inline-block;
  align-self: flex-start;
  margin-top: ${rem(4)};
  text-decoration: underline;

  ${mediaQueryUp.desktop} {
    margin-top: ${rem(16)};
    padding: ${rem(10)} ${rem(20)};
    border: 1px solid ${props => props.theme.palette.neutral.default.eight};
    border-radius: ${props => props.theme.borderRadius.round};
    text-decoration: none;
    white-space: nowrap;
    transition: background-color 0.2s;

    ${mediaHoverable} {
      &:hover {
        background-color: color-mix(in srgb, var(--palette-highlight-primary-dark-background), var(--palette-neutral-control-color-white) var(--palette-neutral-control-opacity-8));
      }
    }
  }
`

const CtaText = styled(BodyText)`
  ${responsiveBodyText}
`

function renderSuperscript(chunks: React.ReactNode) {
  return <sup>{chunks}</sup>
}

function LoyaltyCreditCardLandingBanner() {
  const intl = useIntl()
  const { isEnabled } = useLoyaltyCreditCardFeatureFlag()
  const { impressionRef, onPlacementClick } = useLoyaltyCreditCardPlacementTracking(LOYALTY_CREDIT_CARD_PLACEMENTS.societeLandingPageBanner)

  const eyebrowText = intl.formatMessage({
    id: 'mFzY1LqvOW3YbbaPNqt1',
    defaultMessage: 'Société Credit Card',
    description: 'Small label above the headline on the Société Credit Card promotional banner on the Société loyalty landing page',
  })

  const headingText = intl.formatMessage({
    id: 'uk_vwUrgMB710yTstEhY',
    defaultMessage: '<em>Points</em> that take you places',
    description: 'Headline on the Société Credit Card promotional banner on the Société loyalty landing page, with the first word emphasised',
  }, {
    em: (chunks: React.ReactNode) => <em key="points">{chunks}</em>,
  })

  const ctaText = intl.formatMessage({
    id: 'jO1XUHeBoLjXjQNpLaNn',
    defaultMessage: 'Find out more',
    description: 'Call to action on the Société Credit Card promotional banner on the Société loyalty landing page, linking to the card landing page',
  })

  const benefits = useMemo(() => [
    {
      key: 'points-earn-rate',
      content: intl.formatMessage({
        id: 'DFoznB4GsyHKVnaNBbcD',
        defaultMessage: 'Earn 2 Société Points per $1 spent<sup>1</sup>',
        description: 'First benefit line on the Société loyalty landing-page banner, describing the points earn rate with footnote marker one',
      }, {
        sup: renderSuperscript,
      }),
    },
    {
      key: 'luxury-escapes-credit',
      content: intl.formatMessage({
        id: 'S8lCn5IRG2HOfMPn0cvt',
        defaultMessage: '$150 Luxury Escapes credit<sup>2</sup>',
        description: 'Second benefit line on the Société loyalty landing-page banner, describing the sign-up credit with footnote marker two',
      }, {
        sup: renderSuperscript,
      }),
    },
  ], [intl])

  const accessibleLabel = intl.formatMessage({
    id: 'PuYYGUwmqobbDtLoErke',
    defaultMessage: 'Société Credit Card — points that take you places. Find out more.',
    description: 'Accessible name for the Société Credit Card promotional banner link on the Société loyalty landing page',
  })

  if (!isEnabled) {
    return null
  }

  return <Section background="normal-background">
    <LayoutContainer variant="medium">
      <Banner ref={impressionRef} to={`/${LOYALTY_CREDIT_CARD_PAGE}`} aria-label={accessibleLabel} onClick={onPlacementClick}>
        <Content>
          <Eyebrow variant="small">{eyebrowText}</Eyebrow>
          <Heading variant="heading3" colour="dark-contrast">{headingText}</Heading>
          <BenefitList>
            {benefits.map(benefit => <Benefit key={benefit.key}>
              <LineCheckIcon size={16} />
              <BenefitText variant="small">{benefit.content}</BenefitText>
            </Benefit>)}
          </BenefitList>
          <CtaPill>
            <CtaText variant="small" forwardedAs="span">{ctaText}</CtaText>
          </CtaPill>
        </Content>
        <BannerDrop
          id={MOBILE_IMAGE_ID}
          sources={BANNER_IMAGE_SOURCES}
          position="bottom-right"
          size={MOBILE_IMAGE_DIAMETER}
          alt=""
        />
      </Banner>
    </LayoutContainer>
  </Section>
}

export default LoyaltyCreditCardLandingBanner
